/*!
 * Copyright © 2019-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import { EPermissionStatus, ESpeedUnits, ETrackingDeviceEventCode, IBalloon, IUserPermissions, IUserSettings } from '../../../core/models';
import { IVideoDeviceInfo } from '../../../core/models/video-device-info.interface';
import { EGPSQuality, EHarshDrivingType, EReplaySegmentStatus, IReplayPlotData } from '../../../replay/models';
import { IReplayBalloon } from '../../../replay/models/replay-balloon.interface';
import { ReplayFormattingService } from '../../../replay/services';
import { VideoEventDetailsComponent } from '../video-event-details/video-event-details.component';

@Component({
  selector: 'fm-replay-balloon',
  templateUrl: './replay-balloon.component.html',
  styleUrls: ['./replay-balloon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReplayBalloonComponent implements OnChanges {
  @Input()
  balloon: IReplayBalloon;
  @Input()
  isStreetViewAvailable: boolean;
  @Input()
  userSettings: IUserSettings;
  @Input()
  permissions: IUserPermissions;
  @Input()
  videoDeviceInfo: IVideoDeviceInfo;
  @Output()
  streetViewClicked = new EventEmitter<IBalloon>();
  @Output()
  previousPlotClicked = new EventEmitter<IBalloon>();
  @Output()
  nextPlotClicked = new EventEmitter<IBalloon>();
  @Output()
  requestVideoClicked = new EventEmitter<IBalloon>();

  @ViewChild('videoEventDetails', { read: TemplateRef }) videoEventDetailsTemplate: TemplateRef<any>;

  startTime: string;
  duration: string;
  isDurationMinsOnly: boolean;
  segmentStatus: string;
  showStatsContainer = true;
  videoThumbnailLoaded = false;

  ESpeedUnits = ESpeedUnits;
  EReplaySegmentStatus = EReplaySegmentStatus;
  EHarshDrivingType = EHarshDrivingType;
  EGPSQuality = EGPSQuality;
  EPermissionStatus = EPermissionStatus;

  evBatteryDataStartTime: string;
  evBatteryDataEndTime: string;
  showBatteryLevelRangeSection = false;
  showBatteryLevelHeader = false;

  videoSegmentAvailable = false;
  private _videoEventBaseUrl: string;

  private readonly _statusesToShowBatteryLevelRangeSection = new Set([
    EReplaySegmentStatus.Charging,
    EReplaySegmentStatus.Idle,
    EReplaySegmentStatus.Stopped
  ]);

  constructor(
    private readonly _replayFormattingService: ReplayFormattingService,
    private readonly _dialog: MatLegacyDialog
  ) {}

  ngOnChanges(_: SimpleChanges): void {
    if (this.balloon && this.userSettings) {
      this.startTime = this._replayFormattingService.balloonStartTime(this.balloon.startTime, this.userSettings);
      this.segmentStatus = this._getSegmentStatus();

      this._setDurationString();
      this.showStatsContainer = this._shouldShowStatsContainer();
      this.videoSegmentAvailable = moment(this.balloon.startTime) > moment(this.videoDeviceInfo.earliestRecordingUTC);
    }

    if (this.userSettings) {
      const userLanguage = this.userSettings?.Language ?? window?.navigator?.language ?? 'en-US';
      this._videoEventBaseUrl = `${environment.config.legacyBaseUrl}/${userLanguage}/integratedvideo/`;

      if (this.balloon.segmentEVData) {
        this.evBatteryDataStartTime = this._replayFormattingService.balloonEVTime(this.balloon.segmentDateRange.start, this.userSettings);
        this.evBatteryDataEndTime = this._replayFormattingService.balloonEVTime(this.balloon.segmentDateRange.end, this.userSettings);
        this.showBatteryLevelRangeSection = this._statusesToShowBatteryLevelRangeSection.has(this.balloon.segmentStatus);
      }
    }
  }

  onStreetViewClicked(): void {
    this.streetViewClicked.emit(this.balloon);
  }

  onNextPlotClicked(): void {
    this.nextPlotClicked.emit(this.balloon);
  }

  onPreviousPlotClicked(): void {
    this.previousPlotClicked.emit(this.balloon);
  }

  onVideoThumbnailClicked(): void {
    this._dialog.open(VideoEventDetailsComponent, { panelClass: 'video-event-details-dialog' });
  }

  onVideoThumbnailLoaded(): void {
    this.videoThumbnailLoaded = true;
  }

  onViewEventClicked(): void {
    window.open(`${this._videoEventBaseUrl}${this.balloon.videoDetails.eventId}`, '_blank');
  }

  onRequestVideoClicked(): void {
    this.requestVideoClicked.emit(this.balloon);
  }

  private _setDurationString(): void {
    const { string, isMinsOnly } = this._replayFormattingService.durationString(this.balloon.durationSeconds);
    this.duration = string;
    this.isDurationMinsOnly = isMinsOnly;
  }

  private _shouldShowStatsContainer(): boolean {
    const segmentTypesToNeverShow = [EReplaySegmentStatus.Start, EReplaySegmentStatus.End, EReplaySegmentStatus.Private];
    if (segmentTypesToNeverShow.includes(this.balloon?.segmentStatus)) {
      return false;
    }

    const isMovingWithNoHde =
      this.balloon.segmentStatus === EReplaySegmentStatus.Moving && this.balloon.harshDrivingType === EHarshDrivingType.None;

    return !(isMovingWithNoHde && !this.permissions.showVehicleSpeed);
  }

  private _getSegmentStatus(): string {
    if (this.balloon.segmentStatus === EReplaySegmentStatus.InProgress) {
      this.balloon.segmentStatus = this._getSegmentStatusOfInProgress(this.balloon.plotData);
    }

    return this._replayFormattingService.getReplaySegmentStatusString(this.balloon.segmentStatus);
  }
  private _getSegmentStatusOfInProgress(plotData: IReplayPlotData): EReplaySegmentStatus {
    if (plotData.isPrivate) {
      return EReplaySegmentStatus.Private;
    }
    if (plotData.evBatteryData?.chargingState) {
      return EReplaySegmentStatus.Charging;
    }
    switch (plotData.eventCode) {
      case ETrackingDeviceEventCode.ShutDown: {
        return EReplaySegmentStatus.Stopped;
      }
      case ETrackingDeviceEventCode.IdlingStart:
      case ETrackingDeviceEventCode.IdlingEnd: {
        return EReplaySegmentStatus.Idle;
      }
      default: {
        return EReplaySegmentStatus.Moving;
      }
    }
  }
}
